<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import axios from 'axios';
import { getTmsAPIURL } from '@/config.js';
import headerLogo from '@/assets/images/logo/header.png';

const route = useRoute();
const token = ref(String(route.params.token || ''));
const client = axios.create({ baseURL: getTmsAPIURL() });

const loading = ref(true);
const error = ref('');
const trip = ref(null);
const mapRef = ref(null);
const fitted = ref(false);

const REASONS = {
  auto_stop: 'Ended automatically',
  manual: 'Ended by the driver',
  assignment: 'Ended for an assigned trip',
  logout: 'Ended at sign-out',
  disabled: 'Ended when tracking was turned off',
  remote_disabled: 'Ended when tracking was turned off',
  recovered: 'Recovered after the app stopped',
  max_duration: 'Split at the maximum trip length',
  schedule: 'Work hours ended',
};

function formatWhen(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  return new Date(n).toLocaleString();
}

function formatDistance(meters) {
  const n = Number(meters) || 0;
  if (n >= 1000) return `${(n / 1000).toFixed(1)} km`;
  return `${Math.round(n)} m`;
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/** Driving plus idle equals the clock duration. Minute labels share one minute count. */
function splitTripTime(durationSeconds, movingSeconds) {
  const duration = Math.max(0, Math.round(Number(durationSeconds) || 0));
  const moving = Math.min(duration, Math.max(0, Math.round(Number(movingSeconds) || 0)));
  if (duration < 60) return { duration, moving, idle: duration - moving };
  const durationMin = Math.floor(duration / 60);
  const movingMin = Math.min(durationMin, Math.floor(moving / 60));
  return {
    duration: durationMin * 60,
    moving: movingMin * 60,
    idle: (durationMin - movingMin) * 60,
  };
}

function formatSpeed(mps) {
  const n = Number(mps);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${Math.round(n * 3.6)} km/h`;
}

function formatHeading(degrees) {
  const n = Number(degrees);
  if (!Number.isFinite(n) || n < 0) return '—';
  return `${Math.round(n)}°`;
}

function vehicleText(value) {
  const text = String(value || '').trim();
  return text || 'Not updated';
}

function reasonLabel(value) {
  if (REASONS[value]) return REASONS[value];
  const text = String(value || '').replace(/_/g, ' ').trim();
  if (!text) return '—';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const IDLE_MPS = 2.5;

function markerIcon(text, fill) {
  const width = 62;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="32" viewBox="0 0 ${width} 32"><rect x="1" y="1" width="${width - 2}" height="22" rx="6" fill="${fill}"/><polygon points="${width / 2 - 6},23 ${width / 2 + 6},23 ${width / 2},31" fill="${fill}"/><text x="${width / 2}" y="16" text-anchor="middle" fill="#ffffff" font-family="Arial,sans-serif" font-size="12" font-weight="700">${text}</text></svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}` };
}

function dotIcon(fill) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"><circle cx="7" cy="7" r="5" fill="${fill}" stroke="#ffffff" stroke-width="2"/></svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}` };
}

const START_ICON = markerIcon('Start', '#059669');
const END_ICON = markerIcon('End', '#be123c');
const IDLE_ICON = dotIcon('#d97706');

function pointBearing(from, to) {
  const lat1 = (Number(from.lat) * Math.PI) / 180;
  const lat2 = (Number(to.lat) * Math.PI) / 180;
  const dLng = ((Number(to.lng) - Number(from.lng)) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

function travelHeading(point, previous, next) {
  const stored = Number(point && point.heading);
  if (Number.isFinite(stored) && stored >= 0) return stored;
  if (previous && next) return pointBearing(previous, next);
  if (next) return pointBearing(point, next);
  if (previous) return pointBearing(previous, point);
  return 0;
}

function arrowIcon(degrees) {
  const rotation = ((Number(degrees) % 360) + 360) % 360;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36"><g transform="rotate(${rotation} 18 18)" fill="none" stroke-linecap="round" stroke-linejoin="round"><path d="M8 24 L18 8 L28 24" stroke="#ffffff" stroke-width="8"/><path d="M8 24 L18 8 L28 24" stroke="#7367f0" stroke-width="4"/></g></svg>`;
  const icon = { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}` };
  if (typeof google !== 'undefined' && google.maps) {
    icon.scaledSize = new google.maps.Size(36, 36);
    icon.anchor = new google.maps.Point(18, 18);
  }
  return icon;
}

const pathPoints = computed(() => {
  const rows = trip.value && Array.isArray(trip.value.route) ? trip.value.route : [];
  return rows
    .map((point) => ({ lat: Number(point.lat), lng: Number(point.lng), speed: Number(point.speed) }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
});

const points = computed(() => {
  const rows = trip.value && Array.isArray(trip.value.points) ? trip.value.points : [];
  return rows
    .map((point) => ({
      lat: Number(point.lat),
      lng: Number(point.lng),
      recordedAt: point.recordedAt,
      speed: point.speed,
      heading: point.heading,
      mileageM: point.mileageM,
      address: point.address || '',
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    .slice(0, 10);
});

const playbackIndex = ref(0);
const playing = ref(false);
const paused = ref(false);
let playbackTimer = null;

const pointMeta = computed(() => (trip.value && trip.value.pointPage) || { page: 1, pageSize: 10, pageCount: 1, total: 0 });
const playbackMax = computed(() => Math.max((pointMeta.value.total || points.value.length) - 1, 0));

function pageSize() {
  return pointMeta.value.pageSize || 10;
}

function pageFor(index) {
  return Math.floor(Math.max(0, index) / pageSize()) + 1;
}

const localIndex = computed(() => {
  const local = playbackIndex.value - ((pointMeta.value.page || 1) - 1) * pageSize();
  if (local < 0 || local >= points.value.length) return -1;
  return local;
});

const currentPoint = computed(() => (localIndex.value >= 0 ? points.value[localIndex.value] : null));

const playIcon = computed(() => {
  const point = currentPoint.value;
  if (!point) return null;
  const index = localIndex.value;
  const rows = points.value;
  return arrowIcon(travelHeading(point, rows[index - 1], rows[index + 1]));
});

function stopPlaybackClock() {
  if (playbackTimer) {
    clearInterval(playbackTimer);
    playbackTimer = null;
  }
}

function stopPlayback() {
  stopPlaybackClock();
  playing.value = false;
  paused.value = false;
}

function beginPlaybackClock() {
  stopPlaybackClock();
  playbackTimer = setInterval(() => {
    tickPlayback();
  }, 450);
}

async function startPlayback() {
  const total = pointMeta.value.total || points.value.length;
  if (total < 2) return;
  if (playbackIndex.value >= total - 1) playbackIndex.value = 0;
  playing.value = true;
  paused.value = false;
  const needed = pageFor(playbackIndex.value);
  if (needed !== pointMeta.value.page) {
    await load(needed, { continuePlayback: true });
    return;
  }
  beginPlaybackClock();
}

async function tickPlayback() {
  if (!playing.value || loading.value || pointPaging) return;
  const total = pointMeta.value.total || points.value.length;
  if (playbackIndex.value >= total - 1) {
    stopPlayback();
    return;
  }
  playbackIndex.value += 1;
  const needed = pageFor(playbackIndex.value);
  if (needed !== pointMeta.value.page) {
    stopPlaybackClock();
    await load(needed, { continuePlayback: true });
  }
}

function pausePlayback() {
  stopPlaybackClock();
  playing.value = false;
  paused.value = true;
}

function resumePlayback() {
  if (!paused.value) return;
  startPlayback();
}

function resetPlayback() {
  stopPlayback();
  playbackIndex.value = 0;
  if (pointMeta.value.page > 1) load(1, { keepIndex: true });
}

function selectPoint(index) {
  stopPlayback();
  playbackIndex.value = ((pointMeta.value.page || 1) - 1) * pageSize() + index;
}

function onSeek(event) {
  if (!event.isTrusted) return;
  const next = Math.max(0, Math.min(playbackMax.value, Math.round(Number(event.target.value) || 0)));
  if (next === playbackIndex.value) return;
  stopPlayback();
  playbackIndex.value = next;
  const needed = pageFor(next);
  if (needed !== pointMeta.value.page) load(needed, { keepIndex: true });
}

onBeforeUnmount(stopPlaybackClock);

const stops = computed(() => {
  const rows = trip.value && Array.isArray(trip.value.stops) ? trip.value.stops : [];
  return rows.filter((stop) => Number.isFinite(Number(stop.lat)) && Number.isFinite(Number(stop.lng)));
});

const start = computed(() => pathPoints.value[0] || null);
const end = computed(() => (pathPoints.value.length > 1 ? pathPoints.value[pathPoints.value.length - 1] : null));

function isIdle(point) {
  return Number.isFinite(point.speed) && point.speed < IDLE_MPS;
}

const idlePoints = computed(() => {
  const line = pathPoints.value.filter((point, index, all) => index > 0 && index < all.length - 1 && isIdle(point));
  if (line.length) return line;
  return points.value.filter((point) => isIdle(point));
});
const center = computed(() => start.value || { lat: 3.139, lng: 101.6869 });

const durationSeconds = computed(() => {
  if (!trip.value) return 0;
  const startMs = Number(trip.value.startedAt);
  const endMs = Number(trip.value.endedAt);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0;
  return Math.round((endMs - startMs) / 1000);
});

const avgSpeed = computed(() => {
  if (!trip.value || !(trip.value.movingSeconds > 0)) return 0;
  return trip.value.distanceM / trip.value.movingSeconds;
});

const stats = computed(() => {
  if (!trip.value) return [];
  const times = splitTripTime(durationSeconds.value, trip.value.movingSeconds);
  return [
    ['Distance', formatDistance(trip.value.distanceM)],
    ['Duration', formatDuration(times.duration)],
    ['Driving', formatDuration(times.moving)],
    ['Idle', formatDuration(times.idle)],
    ['Max speed', formatSpeed(trip.value.maxSpeedMps)],
    ['Avg speed', formatSpeed(avgSpeed.value)],
    ['Stops', String(trip.value.stopCount || 0)],
    ['Points', String(trip.value.pointCount || 0)],
  ];
});

async function fitMap() {
  const map = mapRef.value;
  const path = pathPoints.value;
  if (fitted.value || !map || path.length < 2 || typeof google === 'undefined') return;
  const gmap = await map.$mapPromise;
  const bounds = new google.maps.LatLngBounds();
  path.forEach((point) => bounds.extend(point));
  gmap.fitBounds(bounds, 48);
  fitted.value = true;
}

watch(pathPoints, async () => {
  await nextTick();
  fitMap();
});

let pointPaging = false;

async function load(page = 1, options = {}) {
  const continuePlayback = options.continuePlayback === true;
  if (pointPaging) return;
  const paging = page !== 1 || trip.value;
  if (!paging) loading.value = true;
  pointPaging = true;
  error.value = '';
  if (continuePlayback) stopPlaybackClock();
  else if (!options.keepIndex) stopPlayback();
  try {
    const res = await client.get(`public-free-trip/${encodeURIComponent(token.value)}`, {
      params: { page },
    });
    if (res.data && res.data.success && res.data.data) {
      trip.value = res.data.data;
      if (continuePlayback && playing.value) beginPlaybackClock();
    } else {
      if (continuePlayback) {
        playing.value = false;
        paused.value = false;
      }
      error.value = (res.data && res.data.error && res.data.error.message) || 'Could not load this trip';
    }
  } catch (err) {
    if (continuePlayback) {
      playing.value = false;
      paused.value = false;
    }
    if (err && err.response && err.response.status === 404) {
      error.value = 'This trip link was not found.';
    } else if (err && err.response && err.response.status === 429) {
      error.value = 'Too many requests. Wait a minute and try again.';
    } else {
      error.value = 'Could not load this trip.';
    }
  } finally {
    pointPaging = false;
    loading.value = false;
  }
}

load();
</script>

<template>
  <div class="trip-shell">
    <header class="trip-header">
      <div>
        <div class="brand-logo-frame">
          <img class="brand-logo" :src="headerLogo" alt="Igeotech" />
        </div>
        <span class="brand-sub">Free Tracking</span>
      </div>
    </header>

    <main v-if="loading" class="state-card">
      <q-spinner color="primary" size="32px" />
      <div class="state-text">Loading trip…</div>
    </main>

    <main v-else-if="error" class="state-card">
      <div class="state-text">{{ error }}</div>
    </main>

    <main v-else-if="trip" class="trip-body">
      <section class="when-card">
        <div class="when-title">{{ formatWhen(trip.startedAt) }}</div>
        <div class="when-sub">to {{ formatWhen(trip.endedAt) }}</div>
        <div class="when-reason">{{ reasonLabel(trip.endReason) }}</div>
        <div v-if="trip.useType === 'personal' || trip.useType === 'work'" class="when-reason">
          {{ trip.useType === 'work' ? 'Work' : 'Personal' }}
        </div>
      </section>

      <section class="when-card">
        <div class="when-title">Vehicle</div>
        <div class="vehicle-row">
          <span>Vehicle type</span>
          <span :class="{ muted: !trip.vehicleType }">{{ vehicleText(trip.vehicleType) }}</span>
        </div>
        <div class="vehicle-row">
          <span>Plate number</span>
          <span :class="{ muted: !trip.plateNumber }">{{ vehicleText(trip.plateNumber) }}</span>
        </div>
        <div class="vehicle-row">
          <span>Notes</span>
          <span class="vehicle-notes" :class="{ muted: !trip.notes }">{{ vehicleText(trip.notes) }}</span>
        </div>
      </section>

      <section class="map-card">
        <gmap-map
          ref="mapRef"
          :center="center"
          :zoom="13"
          map-type-id="roadmap"
          style="width: 100%; height: 360px"
          :options="{ gestureHandling: 'greedy' }"
          @idle="fitMap"
        >
          <gmap-polyline
            v-if="pathPoints.length > 1"
            :path="pathPoints"
            :options="{ strokeColor: '#1d4ed8', strokeWeight: 5, strokeOpacity: 0.9 }"
          />
          <gmap-marker
            v-for="(point, index) in idlePoints"
            :key="`idle-${index}`"
            :position="point"
            :icon="IDLE_ICON"
            title="Idle"
          />
          <gmap-marker v-if="start" :position="start" :icon="START_ICON" title="Start" />
          <gmap-marker v-if="end" :position="end" :icon="END_ICON" title="End" />
          <gmap-marker
            v-if="currentPoint"
            :position="currentPoint"
            :icon="playIcon"
            :z-index="20"
            title="Now"
          />
          <gmap-marker
            v-for="(stop, index) in stops"
            :key="`stop-${stop.seq || index}`"
            :position="{ lat: Number(stop.lat), lng: Number(stop.lng) }"
            :label="String(index + 1)"
            :title="`Stop ${index + 1}`"
          />
        </gmap-map>
      </section>

      <section v-if="(pointMeta.total || points.length) > 1" class="playbar">
        <button v-if="!playing && !paused" type="button" @click="startPlayback">Start playback</button>
        <button v-if="playing" type="button" @click="pausePlayback">Pause playback</button>
        <button v-if="paused" type="button" @click="resumePlayback">Resume playback</button>
        <input
          type="range"
          min="0"
          :max="playbackMax"
          :value="playbackIndex"
          @input="onSeek"
        />
        <button type="button" @click="resetPlayback">Reset playback</button>
      </section>
      <p v-if="currentPoint && currentPoint.address" class="play-address">{{ currentPoint.address }}</p>

      <h2 v-if="points.length" class="history-title">History Report</h2>
      <section v-if="points.length" class="point-list">
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Speed</th>
              <th>Heading</th>
              <th>Mileage</th>
              <th>Address</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(point, index) in points"
              :id="`public-point-${index}`"
              :key="`point-${index}`"
              :class="{ current: index === localIndex }"
              @click="selectPoint(index)"
            >
              <td>{{ formatWhen(point.recordedAt) }}</td>
              <td>{{ formatSpeed(point.speed) }}</td>
              <td>{{ formatHeading(point.heading) }}</td>
              <td>{{ formatDistance(point.mileageM) }}</td>
              <td>{{ point.address }}</td>
            </tr>
          </tbody>
        </table>
      </section>
      <section v-if="points.length" class="playbar point-pager">
        <button type="button" :disabled="pointMeta.page <= 1" @click="load(pointMeta.page - 1)">Previous</button>
        <span>Page {{ pointMeta.page }} of {{ pointMeta.pageCount }}</span>
        <button type="button" :disabled="pointMeta.page >= pointMeta.pageCount" @click="load(pointMeta.page + 1)">Next</button>
      </section>

      <section class="kpi-row">
        <div v-for="row in stats" :key="row[0]" class="kpi-card">
          <div class="kpi-label">{{ row[0] }}</div>
          <div class="kpi-value">{{ row[1] }}</div>
        </div>
      </section>

      <section v-if="stops.length" class="timeline-card">
        <div class="timeline-title">Stops</div>
        <div v-for="(stop, index) in stops" :key="`row-${stop.seq || index}`" class="timeline-row">
          <span class="timeline-index">{{ index + 1 }}</span>
          <div class="timeline-text">
            <div class="timeline-name">{{ formatWhen(stop.startedAt) }} – {{ formatWhen(stop.endedAt) }}</div>
            <div v-if="stop.durationSeconds != null" class="timeline-meta">{{ formatDuration(stop.durationSeconds) }}</div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<style scoped>
.trip-shell {
  min-height: 100vh;
  background: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0f172a;
}
.trip-header {
  padding: 16px 22px;
  background: #fff;
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.05);
}
.brand-logo-frame {
  width: 130px;
  height: 36px;
  overflow: hidden;
  margin-bottom: 4px;
}
.brand-logo {
  display: block;
  width: 623px;
  height: 90px;
  max-width: none;
  margin: -22px 0 0 -10px;
}
.brand-sub {
  font-size: 11px;
  color: #475569;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.trip-body {
  max-width: 920px;
  margin: 0 auto;
  padding: 20px;
}
.when-card,
.kpi-card,
.timeline-card,
.map-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
}
.when-card {
  padding: 16px 18px;
  margin-bottom: 16px;
}
.when-title {
  font-size: 18px;
  font-weight: 700;
}
.when-notes {
  margin-top: 8px;
  font-size: 13px;
  color: #334155;
  white-space: pre-wrap;
}
.vehicle-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
  font-size: 14px;
}
.vehicle-row span:first-child {
  color: #64748b;
  white-space: nowrap;
}
.vehicle-row span:last-child {
  text-align: right;
}
.vehicle-notes {
  white-space: pre-wrap;
}
.muted {
  color: #94a3b8;
}
.point-pager {
  margin-top: 12px;
}
.when-sub,
.when-reason,
.timeline-meta {
  font-size: 13px;
  color: #475569;
  margin-top: 4px;
}
.map-card {
  overflow: hidden;
  margin-bottom: 16px;
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.kpi-card {
  padding: 14px 16px;
}
.kpi-label {
  font-size: 11px;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.4px;
}
.kpi-value {
  font-size: 18px;
  font-weight: 700;
  margin-top: 4px;
}
.timeline-card {
  padding: 16px 18px;
}
.timeline-title {
  font-weight: 700;
  margin-bottom: 8px;
}
.timeline-row {
  display: flex;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid #f1f5f9;
}
.timeline-index {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.timeline-name {
  font-size: 14px;
}
.playbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.playbar input[type='range'] {
  flex: 1;
}
.playbar button {
  border: 0;
  border-radius: 8px;
  background: #1d4ed8;
  color: #fff;
  padding: 8px 12px;
  font-weight: 600;
  cursor: pointer;
}
.play-address {
  margin: 0 0 12px;
  color: #475569;
  font-size: 13px;
}
.history-title {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
}
.point-list {
  max-height: 360px;
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
  margin-bottom: 16px;
}
.point-list table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.point-list th,
.point-list td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
}
.point-list th:last-child,
.point-list td:last-child {
  white-space: normal;
}
.point-list tbody tr {
  cursor: pointer;
}
.point-list tbody tr.current {
  background: #dbeafe;
}
.state-card {
  max-width: 480px;
  margin: 80px auto;
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  text-align: center;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}
.state-text {
  font-size: 14px;
  color: #334155;
  margin-top: 12px;
}
</style>
